import React, { FC, ReactNode, useContext, useMemo } from "react";
import { useRouter } from "next/router";
import { AppContext } from "context/app";
import { LoginForm as PepLoginForm } from "@pepdirect/ui/login";
import { useLoginUserMutation } from "services/graphql/generated";
import { logUserChange } from "services/analytics";
import { getLinkFromTenant } from "helpers/tenant";
import { getRedirectUrl } from "helpers/routing";
import { LoginDTO } from "types/user";
import { sanitizeUrl } from "@braintree/sanitize-url";

interface LoginFormProps {
  children?: ReactNode;
}

export const LoginForm: FC<LoginFormProps> = ({ children }) => {
  const { tenant } = useContext(AppContext);
  const [loginMutation, { error, loading }] = useLoginUserMutation();
  const router = useRouter();
  const { successRedirectTo, successRedirectSite } = router.query;

  const privacyLink = getLinkFromTenant(tenant, "privacy");
  const termsLink = getLinkFromTenant(tenant, "terms");

  const onSubmit = async (formValue: LoginDTO) => {
    try {
      const { data } = await loginMutation({
        variables: { email: formValue.login, password: formValue.password },
      });
      logUserChange(data?.loginUser?.id);
      const redirectUrl = getRedirectUrl(
        successRedirectTo && sanitizeUrl(successRedirectTo as string),
        successRedirectSite && sanitizeUrl(successRedirectSite as string),
        tenant?.fallbackUrl
      );
      router.push(redirectUrl || "/dashboard/account");
    } catch {
      // previously logged sentry
    }
  };

  const goToForgotPassword = () => {
    router.push({
      pathname: "/forgot-password",
      query: {
        ...(successRedirectTo && { successRedirectTo }),
        ...(successRedirectSite && { successRedirectSite }),
      },
    });
  };

  const mappedErrorMessage = useMemo(() => {
    switch (error?.message) {
      case "not_found":
      case "invalid_password":
        return "Oops, that email and password combination isn't correct. Please check and try again or try resetting your password.";
      default:
        return "An error has occurred. Please try again later.";
    }
  }, [error]);

  return (
    <PepLoginForm
      error={error ? mappedErrorMessage : ""}
      termsUrl={termsLink?.url}
      privacyUrl={privacyLink?.url}
      fallbackUrl={tenant?.fallbackUrl || ""}
      goToForgotPassword={goToForgotPassword}
      isLoading={loading}
      onSubmit={onSubmit}
    >
      {children}
    </PepLoginForm>
  );
};
